import type React from 'react';
import type { QuickFillItem, QuickFillRowMode } from '../../../shared/types';
import type { QuickFillLoadProgress } from './quickFillLoadProgress';
import type {
  ActionBarProps,
  QuickFillLoadingPlaceholderProps,
  QuickFillSidePanelsProps,
} from '../ui/components';
import type { QuickFillNavigationDockProps } from '../ui/components/QuickFillNavigationDock';
import type { QuickFillWorkspaceProps } from '../ui/QuickFillWorkspace';

export interface BuildQuickFillWorkspacePropsInputs {
  modelStatusBanner: React.ReactNode;
  contextMenu: React.ReactNode;
  layout: {
    useDrawerPanels: boolean;
    isShortViewport: boolean;
  };
  loading: {
    isTableLoading: boolean;
    showQuickFillApplyProgress: boolean;
    quickFillApplyProgressMessage: string;
    quickFillApplyProgressDetail: string;
    activeLoadProgressText: string;
    isInitializing: boolean;
    isBuilding: boolean;
    buildProgress: string;
    activeLoadProgress: QuickFillLoadProgress | null;
  };
  data: {
    quickFillData: QuickFillItem[];
    visibleProperties: Array<{ label: string }>;
    rowMode: QuickFillRowMode;
    validationScopeInfo: QuickFillWorkspaceProps['validationScopeInfo'];
    onDismissValidationScope: () => void;
  };
  refs: {
    actionBarRef: React.RefObject<HTMLDivElement | null>;
    tableAreaRef: React.RefObject<HTMLDivElement | null>;
    navigationDockRef: React.RefObject<HTMLDivElement | null>;
  };
  actionBar: ActionBarProps;
  table: QuickFillWorkspaceProps['table'];
  loadingPlaceholder: QuickFillLoadingPlaceholderProps;
  navigationDock: QuickFillNavigationDockProps | null;
  sidePanels: QuickFillSidePanelsProps;
  onTableContextMenu: (event: React.MouseEvent) => void;
}

/**
 * Pure builder for the QuickFillWorkspace props.
 *
 * Assembles the layout, loading, table, navigation and side-panel sub-props
 * from the derived page state and the action-bar/table sub-builders.
 */
export function buildQuickFillWorkspaceProps(
  inputs: BuildQuickFillWorkspacePropsInputs
): QuickFillWorkspaceProps {
  const {
    modelStatusBanner,
    contextMenu,
    layout,
    loading,
    data,
    refs,
    actionBar,
    table,
    loadingPlaceholder,
    navigationDock,
    sidePanels,
    onTableContextMenu,
  } = inputs;

  return {
    modelStatusBanner,
    contextMenu,
    useDrawerPanels: layout.useDrawerPanels,
    isShortViewport: layout.isShortViewport,
    isTableLoading: loading.isTableLoading,
    quickFillData: data.quickFillData,
    visibleProperties: data.visibleProperties,
    rowMode: data.rowMode,
    validationScopeInfo: data.validationScopeInfo,
    onDismissValidationScope: data.onDismissValidationScope,
    showQuickFillApplyProgress: loading.showQuickFillApplyProgress,
    quickFillApplyProgressMessage: loading.quickFillApplyProgressMessage,
    quickFillApplyProgressDetail: loading.quickFillApplyProgressDetail,
    activeLoadProgressText: loading.activeLoadProgressText,
    isInitializing: loading.isInitializing,
    isBuilding: loading.isBuilding,
    buildProgress: loading.buildProgress,
    activeLoadProgress: loading.activeLoadProgress,
    actionBarRef: refs.actionBarRef,
    tableAreaRef: refs.tableAreaRef,
    navigationDockRef: refs.navigationDockRef,
    onTableContextMenu,
    actionBar,
    table,
    shouldRenderTableSurface:
      !loading.isTableLoading || data.quickFillData.length > 0,
    loadingPlaceholder,
    navigationDock,
    sidePanels,
  };
}
